import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** `data-testid` root; derives `${testId}-prev-button`, `-next-button`, `-page-${n}`. */
  testId?: string;
}

function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (current > 3) {
    pages.push('ellipsis');
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push('ellipsis');
  }

  pages.push(total);

  return pages;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
  testId = 'pagination',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <div className={cn('flex items-center gap-1', className)} data-testid={testId}>
      <Button
        variant="outline"
        size="sm"
        className="cursor-pointer gap-1"
        disabled={currentPage <= 1}
        data-testid={`${testId}-prev-button`}
        onClick={() => {
          onPageChange(currentPage - 1);
        }}
      >
        <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        Prev
      </Button>

      {pages.map((page, idx) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${String(idx)}`} className="text-muted-foreground px-2 text-sm">
            ...
          </span>
        ) : (
          <Button
            key={page}
            data-testid={`${testId}-page-${String(page)}`}
            variant={page === currentPage ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-9 w-9 cursor-pointer p-0',
              page === currentPage && 'pointer-events-none',
            )}
            onClick={() => {
              onPageChange(page);
            }}
          >
            {page}
          </Button>
        ),
      )}

      <Button
        variant="outline"
        size="sm"
        className="cursor-pointer gap-1"
        disabled={currentPage >= totalPages}
        data-testid={`${testId}-next-button`}
        onClick={() => {
          onPageChange(currentPage + 1);
        }}
      >
        Next
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </Button>
    </div>
  );
}
